import React, {Component} from 'react';
import PropTypes from 'prop-types';
import {connect} from 'react-redux';
import {compose} from 'redux';
import {withRouter} from 'react-router-dom';
import {getWindow} from 'ssr-window';
import EnvironmentUtils from '@spothero/utils/environment';
import withConfig from 'config/withConfig';
import SearchTracking from 'utils/search-tracking';
import {resetFilters} from 'filters/filters-actions';
import {notify} from 'notification/notification-actions';
import {push} from 'router/router-actions';
import {routeToSpotDetails} from 'router/router-utils';
import {getSpotsRadial, getSpotsTiled} from 'spots/spots-actions';
import {
    panMap,
    setSearchMapInstance,
    setZoom,
    setNoSpotsFound,
} from 'search/search-actions';
import {GOOGLE_MAPS_LAT_LNG} from '../search-prop-types';
import SpotMap from './SpotMap';

const window = getWindow();
const isBrowser = EnvironmentUtils.isBrowser();
const gmaps = isBrowser ? window.google.maps : null;

class SearchMap extends Component {
    static propTypes = {
        history: PropTypes.shape({
            location: PropTypes.object,
        }),
        isAdmin: PropTypes.bool,
        isMobile: PropTypes.bool.isRequired,
        searchRequestId: PropTypes.string,
        destination: PropTypes.shape({
            title: PropTypes.string.isRequired,
            latitude: PropTypes.number.isRequired,
            longitude: PropTypes.number.isRequired,
            position: GOOGLE_MAPS_LAT_LNG,
        }).isRequired,
        spots: PropTypes.arrayOf(PropTypes.object),
        selectedSpot: PropTypes.object,
        zoom: PropTypes.number,
        starts: PropTypes.string,
        ends: PropTypes.string,
        isMonthly: PropTypes.bool,
        idealDistance: PropTypes.number,
        overrideIdealZoom: PropTypes.bool,
        latitude: PropTypes.number,
        longitude: PropTypes.number,
        searchString: PropTypes.string,
        hideRatesOnPins: PropTypes.bool,
        isFiltered: PropTypes.bool,
        mapLoadingIncrement: PropTypes.number,
        requestIncrements: PropTypes.number,
        loadRadialSpots: PropTypes.func,
        loadTiledSpots: PropTypes.func,
        resetFiltersClick: PropTypes.func,
        findParkingNearMe: PropTypes.func,
        setSearchMap: PropTypes.func,
        onPan: PropTypes.func,
        onZoom: PropTypes.func,
        kmlMapLayerEnabled: PropTypes.bool,
        kmlMapLayerURL: PropTypes.string,
        staticBaseUrl: PropTypes.string.isRequired,
        searchType: PropTypes.string.isRequired,
        geolocated: PropTypes.bool,
        isSoldOutEnabled: PropTypes.bool,
        pushTo: PropTypes.func.isRequired,
        sendNotification: PropTypes.func.isRequired,
        onShowDetailsClick: PropTypes.func.isRequired,
        onNoSpotsFound: PropTypes.func.isRequired,
    };

    _onMarkerClick = spotId => {
        const {
            history: {location},
            onShowDetailsClick,
            pushTo,
        } = this.props;

        onShowDetailsClick(spotId);

        routeToSpotDetails({
            method: pushTo,
            location,
            spotId,
            historyState: {referrer: 'pin'},
        });
    };

    _onMapMoved = ({zoomed = false, autoZoomed = false}) => {
        SearchTracking.updateAction(
            zoomed ? (autoZoomed ? 'AUTO_ZOOM' : 'ZOOM') : 'PAN'
        );
    };

    _onNoResults = () => {
        this.props.onNoSpotsFound(true);
    };

    render() {
        const {
            isMobile,
            searchRequestId,
            spots,
            zoom,
            onPan,
            onZoom,
            isMonthly,
            isFiltered,
            destination,
            searchString,
            latitude,
            longitude,
            starts,
            ends,
            hideRatesOnPins,
            idealDistance,
            overrideIdealZoom,
            mapLoadingIncrement,
            requestIncrements,
            loadRadialSpots,
            loadTiledSpots,
            findParkingNearMe,
            resetFiltersClick,
            kmlMapLayerURL,
            kmlMapLayerEnabled,
            selectedSpot,
            setSearchMap,
            staticBaseUrl,
            searchType,
            geolocated,
            sendNotification,
            isAdmin,
            isSoldOutEnabled,
        } = this.props;

        return (
            <div className="SearchMap">
                <SpotMap
                    ref={node => {
                        this._spotMap = node;
                    }}
                    isAdmin={isAdmin}
                    isSoldOutEnabled={isSoldOutEnabled}
                    isMobile={isMobile}
                    isNoResultsModalEnabled={isMobile}
                    searchRequestId={searchRequestId}
                    isFiltered={isFiltered}
                    zoom={zoom}
                    onPan={onPan}
                    onZoom={onZoom}
                    idealDistance={idealDistance}
                    overrideIdealZoom={overrideIdealZoom}
                    destination={destination}
                    spots={spots}
                    isMonthly={isMonthly}
                    hideRatesOnPins={hideRatesOnPins}
                    searchString={searchString}
                    latitude={latitude}
                    longitude={longitude}
                    starts={starts}
                    ends={ends}
                    setSearchMap={setSearchMap}
                    mapLoadingIncrement={mapLoadingIncrement}
                    requestIncrements={requestIncrements}
                    onMarkerClick={this._onMarkerClick}
                    loadRadialSpots={loadRadialSpots}
                    loadTiledSpots={loadTiledSpots}
                    resetFiltersClick={resetFiltersClick}
                    findParkingNearMe={findParkingNearMe}
                    kmlMapLayerURL={kmlMapLayerURL}
                    kmlMapLayerEnabled={kmlMapLayerEnabled}
                    selectedSpot={selectedSpot}
                    onMapMoved={this._onMapMoved}
                    staticBaseUrl={staticBaseUrl}
                    searchType={searchType}
                    geolocated={geolocated}
                    sendNotification={sendNotification}
                    onNoResults={this._onNoResults}
                />
            </div>
        );
    }
}

const mapStateToProps = state => {
    const {
        search: {
            data: {
                currentIncrement,
                requestIncrements,
                zoom,
                overrideIdealZoom,
                destinationTitle,
                selectedSpot,
                kmlMapLayerEnabled,
                kmlMapLayerURL,
                searchType,
                geolocated,
            },
        },
        searchRequest: {
            id,
            starts,
            ends,
            monthly,
            latitude,
            longitude,
            search_string: searchString,
            ideal_search_distance: idealDistance,
        },
        filters: {numActive},
        user: {
            data: {isAdmin},
        },
    } = state;

    return {
        isAdmin,
        searchRequestId: id,
        starts,
        ends,
        isMonthly: monthly,
        selectedSpot,
        isFiltered: numActive > 0,
        searchString,
        latitude,
        longitude,
        destination: {
            title: destinationTitle,
            latitude,
            longitude,
            position: isBrowser ? new gmaps.LatLng(latitude, longitude) : null,
        },
        zoom,
        idealDistance,
        overrideIdealZoom,
        mapLoadingIncrement: currentIncrement,
        requestIncrements,
        kmlMapLayerEnabled,
        kmlMapLayerURL,
        searchType,
        geolocated,
    };
};

const mapDispatchToProps = {
    loadRadialSpots: getSpotsRadial,
    loadTiledSpots: getSpotsTiled,
    resetFiltersClick: resetFilters,
    setSearchMap: setSearchMapInstance,
    onZoom: setZoom,
    onPan: panMap,
    onNoSpotsFound: setNoSpotsFound,
    pushTo: push,
    sendNotification: notify,
};

const enhance = compose(
    withConfig(['isMobile', 'staticBaseUrl']),
    withRouter,
    connect(mapStateToProps, mapDispatchToProps)
);

export default enhance(SearchMap);
